// Client copy of lib/dealPricing.js — the ONE place that decides which stored
// fields mean "original price", "current price" and "percent off".
// Keep in sync with the server module; both have matching unit tests.

const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : parseFloat(String(value).replace(/[$,]/g, ''));
  return Number.isFinite(n) && n > 0 ? n : null;
};

const firstNumber = (...values) => {
  for (const v of values) {
    const n = toNumber(v);
    if (n !== null) return n;
  }
  return null;
};

const roundMoney = (n) => Math.round(n * 100) / 100;

/**
 * @returns {{ originalPrice: number|null, salePrice: number|null, percentOff: number,
 *             savings: number|null, promoCode: string|null, hasDiscount: boolean }}
 */
export const getDealPricing = (deal = {}) => {
  const d = deal || {};
  const originalPrice = firstNumber(d.orig_price, d.retail_price);
  let salePrice = firstNumber(d.promoPrice, d.promo_price, d.disc_price, d.discount_price);
  const storedPercent = firstNumber(d.percOff, d.percent_discount);

  if (salePrice === null && originalPrice !== null && storedPercent !== null && storedPercent < 100) {
    salePrice = roundMoney(originalPrice * (1 - storedPercent / 100));
  }

  let percentOff = 0;
  if (originalPrice !== null && salePrice !== null && originalPrice > salePrice) {
    percentOff = Math.round(((originalPrice - salePrice) / originalPrice) * 100);
  } else if (storedPercent !== null && (originalPrice === null || salePrice === null)) {
    percentOff = Math.round(storedPercent);
  }

  const hasDiscount = originalPrice !== null && salePrice !== null && originalPrice > salePrice;
  const code = d.promoCode || d.promo_code;

  return {
    originalPrice: originalPrice !== null ? roundMoney(originalPrice) : null,
    salePrice: salePrice !== null ? roundMoney(salePrice) : null,
    percentOff,
    savings: hasDiscount ? roundMoney(originalPrice - salePrice) : null,
    promoCode: typeof code === 'string' && code.trim() ? code.trim() : null,
    hasDiscount,
  };
};

/** "$12.50", or '' when the price is unknown (so nothing renders as "$undefined"). */
export const formatPrice = (n) => (Number.isFinite(n) ? `$${n.toFixed(2)}` : '');

export default getDealPricing;
